import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircleIcon, LoaderCircleIcon, SaveIcon } from 'lucide-react';
import type { AdminProfile } from '../../hooks/useAdminAccess';
import { createEmptyProspectForm, prospectFormFromRow, type ProspectFormValues } from '../../lib/prospects/schema';
import type { ProspectIdentityValues } from '../../lib/prospectDuplicateCheck';
import { isAdmin, isValidUuid, type CrmRecord } from '../../lib/crm';
import { ProspectService } from '../../lib/supabase/services/prospects.service';
import { ProspectDuplicateNotice } from './ProspectDuplicateNotice';
import { useProspectDuplicateCheck } from './useProspectDuplicateCheck';
interface EditProspectPageProps {
  profile: AdminProfile;
  prospectId: string;
}
export function EditProspectPage({
  profile,
  prospectId
}: EditProspectPageProps) {
  const [form, setForm] = useState<ProspectFormValues>(createEmptyProspectForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const identity = useMemo<ProspectIdentityValues>(() => ({
    businessName: form.business_name,
    phone: form.phone,
    whatsapp: form.whatsapp,
    email: form.email,
    instagram: form.instagram_url,
    facebook: form.facebook_url,
    website: form.website_url,
    googleMaps: form.google_maps_url
  }), [form.business_name, form.phone, form.whatsapp, form.email, form.instagram_url, form.facebook_url, form.website_url, form.google_maps_url]);
  const duplicateState = useProspectDuplicateCheck(identity, prospectId);
  useEffect(() => {
    void load();
  }, [prospectId]);
  async function load() {
    if (!isValidUuid(prospectId) || !isAdmin(profile.role)) {
      setError('No tenés acceso a esta edición.');
      setLoading(false);
      return;
    }
    try {
      const prospect = await ProspectService.get(prospectId);
      if (!prospect) {
        setError('No fue posible cargar el prospecto solicitado.');
        return;
      }
      setForm(prospectFormFromRow(prospect as CrmRecord));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'No fue posible cargar el prospecto solicitado.');
    } finally {
      setLoading(false);
    }
  }
  function update(field: keyof ProspectFormValues, content: string) {
    setForm(current => ({
      ...current,
      [field]: content
    }));
    setError(null);
  }
  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting || !isAdmin(profile.role)) return;
    setSubmitting(true);
    setError(null);
    try {
      await ProspectService.update(prospectId, form, profile.id);
      navigate(`/admin/prospectos/${prospectId}`);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'No fue posible actualizar el prospecto.');
    } finally {
      setSubmitting(false);
    }
  }
  if (loading) {
    return <div className="px-4 py-8 text-sm text-slate-400" data-id="element-225">Cargando edición…</div>;
  }
  if (!isAdmin(profile.role) || error === 'No tenés acceso a esta edición.') {
    return <div className="mx-auto max-w-3xl px-4 py-8" data-id="element-226">
        <section className="rounded-2xl border border-amber-300/20 bg-amber-300/10 p-6 text-amber-50" data-id="element-227">
          {error}
        </section>
      </div>;
  }
  return <div className="mx-auto max-w-5xl px-4 py-7 sm:px-6 lg:px-8" data-id="element-228">
      <header className="border-b border-white/10 pb-6" data-id="element-229">
        <p className="font-orbitron text-xs font-bold tracking-[.18em] text-cyan-200" data-id="element-230">
          CRM COMERCIAL
        </p>
        <h1 className="mt-2 text-3xl font-bold text-white" data-id="element-231">Editar prospecto</h1>
        <p className="mt-2 text-sm text-slate-400" data-id="element-232">
          Solo el nombre del negocio es obligatorio. Las coincidencias son
          informativas.
        </p>
      </header>

      <form className="mt-6 space-y-6" onSubmit={handleSubmit} data-id="element-233">
        <FormSection title="Información principal" data-id="element-234">
          <EditableField label="Nombre del negocio" required value={form.business_name} onChange={value => update('business_name', value)} data-id="element-235" />
          <EditableField label="Nombre del contacto" value={form.contact_name} onChange={value => update('contact_name', value)} data-id="element-236" />
          <EditableField label="Puesto o rol del contacto" value={form.contact_role} onChange={value => update('contact_role', value)} data-id="element-237" />
          <EditableField label="Teléfono" type="tel" value={form.phone} onChange={value => update('phone', value)} data-id="element-238" />
          <EditableField label="WhatsApp" type="tel" value={form.whatsapp} onChange={value => update('whatsapp', value)} data-id="element-239" />
          <EditableField label="Correo" type="email" value={form.email} onChange={value => update('email', value)} data-id="element-240" />
          <EditableField label="Provincia" value={form.province} onChange={value => update('province', value)} data-id="element-241" />
          <EditableField label="Ciudad" value={form.city} onChange={value => update('city', value)} data-id="element-242" />
          <EditableField label="Dirección" value={form.address} onChange={value => update('address', value)} data-id="element-243" />
        </FormSection>

        <FormSection title="Origen e interés" data-id="element-244">
          <EditableField label="ID de nicho" value={form.niche_id} onChange={value => update('niche_id', value)} data-id="element-245" />
          <EditableField label="ID de fuente" value={form.source_id} onChange={value => update('source_id', value)} data-id="element-246" />
          <SelectField label="Nivel de interés" value={form.interest_level} onChange={value => update('interest_level', value)} options={[['cold', 'Frío'], ['warm', 'Tibio'], ['hot', 'Caliente']]} data-id="element-247" />
          <EditableField label="Interés de producto o servicio" value={form.product_interest} onChange={value => update('product_interest', value)} data-id="element-248" />
          <EditableField label="Valor estimado" inputMode="decimal" value={form.estimated_value} onChange={value => update('estimated_value', value)} data-id="element-249" />
          <SelectField label="Moneda" value={form.currency} onChange={value => update('currency', value)} options={[['CRC', 'CRC · Colón costarricense'], ['USD', 'USD · Dólar estadounidense']]} data-id="element-250" />
        </FormSection>

        <FormSection title="Identificadores digitales" data-id="element-251">
          <EditableField label="Sitio web" type="url" value={form.website_url} onChange={value => update('website_url', value)} data-id="element-252" />
          <EditableField label="Instagram" type="url" value={form.instagram_url} onChange={value => update('instagram_url', value)} data-id="element-253" />
          <EditableField label="Facebook" type="url" value={form.facebook_url} onChange={value => update('facebook_url', value)} data-id="element-254" />
          <EditableField label="Google Maps" type="url" value={form.google_maps_url} onChange={value => update('google_maps_url', value)} data-id="element-255" />
        </FormSection>

        <ProspectDuplicateNotice state={duplicateState} similarConfirmed={false} onSimilarConfirmed={() => undefined} nonBlocking data-id="element-256" />

        <FormSection title="Investigación y notas" data-id="element-257">
          <TextAreaField label="Notas de investigación" value={form.research_notes} onChange={value => update('research_notes', value)} data-id="element-258" />
          <TextAreaField label="Notas" value={form.notes} onChange={value => update('notes', value)} data-id="element-259" />
        </FormSection>

        {error && <p role="alert" className="flex gap-2 rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm text-red-100" data-id="element-260">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-261" />
            {error}
          </p>}

        <div className="flex flex-wrap justify-end gap-3 border-t border-white/10 pt-5" data-id="element-262">
          <a href={`/admin/prospectos/${prospectId}`} className="inline-flex min-h-11 items-center rounded-xl border border-white/10 px-4 text-sm font-semibold text-slate-200" data-id="element-263">
            Cancelar
          </a>
          <button type="submit" disabled={submitting} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-cyan-300 px-5 text-sm font-bold text-[#06202a] disabled:opacity-50" data-id="element-264">
            {submitting ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-265" /> : <SaveIcon className="h-4 w-4" data-id="element-266" />}
            Guardar cambios
          </button>
        </div>
      </form>
    </div>;
}
function FormSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-2xl border border-white/10 bg-[#0b1728] p-5 sm:p-6" data-id="element-267">
      <h2 className="text-lg font-bold text-white" data-id="element-268">{title}</h2>
      <div className="mt-5 grid gap-4 md:grid-cols-2" data-id="element-269">{children}</div>
    </section>;
}
function EditableField({
  label,
  value,
  onChange,
  type = 'text',
  inputMode,
  required = false
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
  required?: boolean;
}) {
  return <label data-id="element-270">
      <span className="mb-2 block text-sm font-medium text-slate-200" data-id="element-271">
        {label}
      </span>
      <input required={required} type={type} inputMode={inputMode} value={value} onChange={event => onChange(event.target.value)} className="min-h-11 w-full rounded-xl border border-white/10 bg-[#07111f] px-3 text-sm text-white" data-id="element-272" />
    </label>;
}
function SelectField({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: [string, string][];
}) {
  return <label data-id="element-273">
      <span className="mb-2 block text-sm font-medium text-slate-200" data-id="element-274">
        {label}
      </span>
      <select value={value} onChange={event => onChange(event.target.value)} className="min-h-11 w-full rounded-xl border border-white/10 bg-[#07111f] px-3 text-sm text-white" data-id="element-275">
        <option value="" data-id="element-276">Sin asignar</option>
        {options.map(([id, name]) => <option key={id} value={id} data-id="element-277">
            {name}
          </option>)}
      </select>
    </label>;
}
function TextAreaField({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return <label className="md:col-span-2" data-id="element-278">
      <span className="mb-2 block text-sm font-medium text-slate-200" data-id="element-279">
        {label}
      </span>
      <textarea value={value} onChange={event => onChange(event.target.value)} rows={4} className="w-full resize-y rounded-xl border border-white/10 bg-[#07111f] px-3 py-3 text-sm text-white" data-id="element-280" />
    </label>;
}
function navigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}